/** @module csi/def/Rule */
define([
	"dojo/_base/declare",
	"csi",
	"csi/_base/underscore",
	"csi/config",
	"csi/middleware/rollup/rollupService",
	"csi/util",
	"csi/xml"
], function (declare, csi, _, config, { hasAttributeDependency, evaluate }, util, xml) {

	var _getValue = function (val) {
		// convert 'true' and 'false' values into strings for comparison
		return ((val === true) || (val === false)) ? String(val) : val;
	};

	return declare(null, {
		/** @property {boolean} bApplyToForm - apply Rule when processed on Form views */
		bApplyToForm: false,

		constructor: function (def) {
			// rollup expression syntax
			this.m_exp = util.getProperty(def, "Exp");

			// alternate syntax
			this.m_setting = util.getProperty(def, "Setting");
			this.m_id1 = util.getProperty(def, "Id1");
			this.m_op = util.getProperty(def, "Op");
			this.m_id2 = util.getProperty(def, "Id2");
			this.m_value = util.getProperty(def, "Value");

			// optional
			this.bApplyToForm = util.getBooleanProperty(def, "ApplyToForm", false);
			this.m_class = util.getProperty(def, "Class");
			this.m_path = util.getProperty(def, "Path");
		},

		isValid: function () {
			var bValue = (this.m_value != null);
			var bExp = (this.m_exp != null);

			if ((bExp && (this.m_id1 || this.m_op || bValue)) || (bValue && this.m_id2)) {
				return false;
			}

			// valid if expression, or if operator and at least one operand is specified; the second
			// operand can be filled in by context
			return bExp || (this.m_op && (bValue || this.m_id1 || this.m_id2 || this.m_setting));
		},

		hasAttributeDependency: function (idAttr) {
			// summary: return true if Rule relies on given attribute
			return (this.m_id1 == idAttr) ||
				(this.m_id2 == idAttr) ||
				(_.isString(this.m_exp) && hasAttributeDependency(this.m_exp, idAttr));
		},

		/**
		 * Test the rule
		 * @param {Node} node 
		 * @param {Cache} cache 
		 * @param {Context} ctx 
		 * @param {Heading} [h]
		 * @return {boolean}
		 */
		passes: function (node, cache, ctx, h) {
			// NOTE: This is similar to csi/service/ruleService#rulePasses with a few exceptions:
			// - this supports expression functions and constants
			// - this can evaluate expressions on attributes (Id1, Id2)
			var nodePath = cache ? cache.getPathNode(node, this.m_path, true) : null;

			if (this.m_exp != null) {
				if (_.isString(this.m_exp)) {
					// if string then evaluate as rollup expression
					return (evaluate(this.m_exp, nodePath, cache, false, ctx) == true);
				} else if (_.isFunction(this.m_exp)) {
					// if function then return result of call
					return (this.m_exp)(ctx);
				}

				// return constant
				return this.m_exp;
			}

			if (!nodePath) {
				return false;
			}

			if (this.m_id1) {
				if (this.m_op == xml.OP_EXISTS) {
					return xml.nodeHasAttribute(nodePath, this.m_id1);
				} else if (this.m_op == xml.OP_NOT_EXISTS) {
					return !xml.nodeHasAttribute(nodePath, this.m_id1);
				}
			}

			const attr = (id) => {
				let result = _getValue((ctx && ctx.rp) ? ctx.rp.evaluateAttribute(id, nodePath, true) : nodePath[id]);

				// if the value is a container, and then extract the value from it
				if (h && (h.m_id === id) && ctx.hasContainerKey()) {
					result = result[ctx.keyValue];
				}

				return result;
			};

			var v1 = this.m_id1 ? attr(this.m_id1) : _getValue(config.getNodeSetting(nodePath, this.m_setting));
			var v2 = this.m_id2 ? attr(this.m_id2) : _getValue(this.m_value);

			switch (this.m_op) {
				case xml.OP_EQUALS: return v1 == v2;
				case xml.OP_NOT_EQUALS: return v1 != v2;
				case xml.OP_LESS_THAN: return v1 < v2;
				case xml.OP_LESS_EQUAL: return v1 <= v2;
				case xml.OP_GREATER_THAN: return v1 > v2;
				case xml.OP_GREATER_EQUAL: return v1 >= v2;
				case xml.OP_NOT_NULL: return !(v1 == null || v1 == csi.EMPTY_URL);
				case xml.OP_NULL: return (v1 == null || v1 == csi.EMPTY_URL);
				case "OR_EQ": return _.some(v2.split(";"), function (v) { return v1 == v; });
			}

			console.warn("Rule.passes: Unknown operation = ", this.m_op);
			return false;
		}
	});
});